import { supabase } from './supabase';
import { hasAllRequiredClinicalPhotos } from '../case-dentist/scanUploadConfig';
import {
  resolveCaseFileTechnicalName,
  resolveDocumentDisplayName,
  resolvePhotoDisplayName,
  resolveScanDisplayName,
} from './caseFileDisplayLabels';

export const formatCaseFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
};

/** Garde la ligne la plus récente par clé métier (évite les doublons historiques). */
function pickLatestActivePerKey(rows, keyField) {
  const byKey = new Map();
  for (const row of rows || []) {
    const key = row[keyField];
    if (!key) continue;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, row);
      continue;
    }
    const prevDate = new Date(prev.upload_date).getTime();
    const rowDate = new Date(row.upload_date).getTime();
    if (rowDate > prevDate || (rowDate === prevDate && String(row.id) > String(prev.id))) {
      byKey.set(key, row);
    }
  }
  return [...byKey.values()];
}

/**
 * Same structure as the array built in case-detail `fetchFiles`, without React state.
 */
export async function getAllCaseFilesList(caseId) {
  const { data: scans } = await supabase
    .from('case_scans')
    .select('*, uploaded_by:user_profiles(full_name)')
    .eq('case_id', caseId)
    .eq('is_active', true);

  const { data: photos } = await supabase
    .from('case_photos')
    .select('*, uploaded_by:user_profiles(full_name)')
    .eq('case_id', caseId)
    .eq('is_active', true);

  const { data: documents } = await supabase
    .from('case_documents')
    .select('*, uploaded_by:user_profiles(full_name)')
    .eq('case_id', caseId)
    .eq('is_active', true);

  const uniqueScans = pickLatestActivePerKey(scans, 'scan_type');
  const uniquePhotos = pickLatestActivePerKey(photos, 'photo_type');

  return [
    ...uniqueScans.map((s) => {
      const technicalName = resolveCaseFileTechnicalName(s.file_name, s.file_path);
      return {
        id: s.id,
        name: resolveScanDisplayName(s),
        technicalName,
        originalName: s.original_file_name || null,
        scanType: s.scan_type || null,
        type: 'scan',
        category: 'scans',
        size: formatCaseFileSize(s.file_size),
        uploadedBy: s.uploaded_by?.full_name || 'Inconnu',
        uploadDate: new Date(s.upload_date).toLocaleDateString('fr-FR'),
        version: s.version,
        filePath: s.file_path,
      };
    }),
    ...uniquePhotos.map((p) => {
      const technicalName = resolveCaseFileTechnicalName(p.file_name, p.file_path);
      return {
        id: p.id,
        name: resolvePhotoDisplayName(p),
        technicalName,
        photoKey: p.photo_type || null,
        type: 'image',
        category: 'photos',
        size: formatCaseFileSize(p.file_size),
        uploadedBy: p.uploaded_by?.full_name || 'Inconnu',
        uploadDate: new Date(p.upload_date).toLocaleDateString('fr-FR'),
        version: p.version,
        filePath: p.file_path,
        thumbnail: p.file_path,
      };
    }),
    ...(documents || []).map((d) => {
      const technicalName = resolveCaseFileTechnicalName(d.file_name, d.file_path);
      return {
        id: d.id,
        name: resolveDocumentDisplayName(d),
        technicalName,
        type: 'pdf',
        category: 'documents',
        size: formatCaseFileSize(d.file_size),
        uploadedBy: d.uploaded_by?.full_name || 'Inconnu',
        uploadDate: new Date(d.upload_date).toLocaleDateString('fr-FR'),
        version: d.version,
        filePath: d.file_path,
      };
    }),
  ];
}

/**
 * File-based phase 1 milestone flags (Scans + Photos) — same rules as case-detail.
 */
export async function syncPhase1FileMilestones(caseId, latestFiles) {
  const { data: phasesData, error } = await supabase
    .from('case_timeline_phases')
    .select('id, milestones:phase_milestones(*)')
    .eq('case_id', caseId)
    .eq('group', '1')
    .eq('phase_order', 1)
    .single();

  if (error || !phasesData) {
    console.error('Error fetching phase 1 for milestone update:', error);
    return;
  }

  const milestones = phasesData.milestones || [];
  const hasScans = latestFiles.some((f) => f.category === 'scans');
  const uploadedPhotosByKey = {};
  for (const f of latestFiles) {
    if (f.category === 'photos' && f.photoKey) uploadedPhotosByKey[f.photoKey] = f;
  }
  const hasPhotos = hasAllRequiredClinicalPhotos(uploadedPhotosByKey);
  const now = new Date().toISOString();

  for (const m of milestones) {
    let newCompleted = m.is_completed;

    if (m.milestone_name === 'Scans du patient téléchargés') {
      newCompleted = hasScans;
    } else if (m.milestone_name === 'Photos cliniques soumises') {
      newCompleted = hasPhotos;
    }

    if (newCompleted !== m.is_completed) {
      await supabase
        .from('phase_milestones')
        .update({
          is_completed: newCompleted,
          completed_date: newCompleted ? now : null,
          updated_at: now,
        })
        .eq('id', m.id);
    }
  }
}

/**
 * Re-fetch file list and sync phase-1 file milestones (after uploads from dentist wizard).
 */
export async function refreshCaseFileMilestones(caseId) {
  const allFiles = await getAllCaseFilesList(caseId);
  await syncPhase1FileMilestones(caseId, allFiles);
  return allFiles;
}
